import axios from 'axios';
import { getApiHost } from 'src/utils/ssoReferrer';

export interface DivisionMembership {
  id: string;
  name: string;
  companyName: string;
  role: string;
  appAccess: Record<string, string>;
  isActive: boolean;
}

export interface V2LoginResponse {
  userId: string;
  email: string;
  name: string;
  divisions: DivisionMembership[];
  lastActiveDivisionId: string;
  sessionToken: string;
}

export interface V2TokenResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  divisionId: string;
  divisionName: string;
  // Optional so the frontend tolerates older backends mid-deploy
  // and falls back to a plain reload(). Populated when the division's
  // VendorId resolves to a positive int, otherwise omitted.
  defaultBeaconClientId?: number;
}

export interface V2ValidateResponse {
  authenticated: boolean;
  reason?: string;
  user?: {
    userId: string;
    email: string;
    name: string;
  };
  division?: DivisionMembership;
  divisions?: DivisionMembership[];
  sessionToken?: string;
}

export const v2LoginFederated = async (
  accessToken: string,
  idToken: string,
  referrer?: string
): Promise<V2LoginResponse> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/login/federated`, {
    accessToken,
    idToken,
    referrer
  });
  return response.data;
};

export const v2IssueToken = async (
  sessionToken: string,
  divisionId: string,
  referrer?: string
): Promise<V2TokenResponse> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/token`, {
    sessionToken,
    divisionId,
    referrer
  }, {
    withCredentials: true
  });
  return response.data;
};

export const v2GetDivisions = async (): Promise<{
  divisions: DivisionMembership[];
  activeDivisionId: string;
}> => {
  const baseUrl = getApiHost();
  const response = await axios.get(`https://${baseUrl}/api/auth/v2/divisions`);
  return response.data;
};

export const v2SwitchDivision = async (newDivisionId: string): Promise<V2TokenResponse & { previousTokenRevoked: boolean }> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/switch-division`, {
    newDivisionId
  });
  return response.data;
};

export const v2Validate = async (): Promise<V2ValidateResponse> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/validate`, {}, {
    withCredentials: true
  });
  return response.data;
};

export const v2Logout = async (): Promise<{ success: boolean }> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/logout`);
  return response.data;
};

export const v2Refresh = async (refreshToken: string, divisionId: string): Promise<V2TokenResponse> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/refresh`, {
    refreshToken,
    divisionId
  });
  return response.data;
};

export const v2OtpSend = async (email: string): Promise<{ success: boolean }> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/otp/send`, { email });
  return response.data;
};

export const v2OtpVerify = async (email: string, code: string): Promise<V2LoginResponse> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/otp/verify`, { email, code });
  return response.data;
};

/**
 * Pick the default division for auto-login.
 * Backend already resolves lastActiveDivisionId with 3-tier fallback
 * (Redis last-active -> sl-users.divisionId -> oldest membership)
 * and returns divisions sorted by createdAt ascending.
 */
export function resolveDefaultDivision(
  divisions: DivisionMembership[],
  lastActiveDivisionId: string | undefined
): DivisionMembership {
  if (lastActiveDivisionId) {
    const preferred = divisions.find(d => d.id === lastActiveDivisionId);
    if (preferred) return preferred;
  }
  return divisions[0];
}

export const fetchSubscriptions = async (accessToken: string, appName: string): Promise<any> => {
  const baseUrl = getApiHost();
  const response = await axios.get(`https://${baseUrl}/api/auth/v2/subscriptions`, {
    params: { appName },
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });
  return response.data;
};
